// src/components/modals/CreateCourseModal.jsx
import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearch } from '../../contexts/SearchContext';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

/* ── Shared field label ────────────────────────────────────── */
const FieldLabel = ({ htmlFor, required, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 text-right"
  >
    {children} {required && <span className="text-red-400 normal-case">*</span>}
  </label>
);

/* ── Error message ─────────────────────────────────────────── */
const FieldError = ({ msg }) =>
  msg ? (
    <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1 justify-end">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {msg}
    </p>
  ) : null;

/* ── InputField ────────────────────────────────────────────── */
const InputField = React.forwardRef(function InputField(
  {
    label,
    name,
    type = 'text',
    placeholder,
    value,
    onChange,
    onBlur,
    error,
    required = false,
    dir = 'rtl',
    ...props
  },
  ref
) {
  const [focused, setFocused] = useState(false);
  const isDate = type === 'date';

  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={name} required={required}>
        {label}
      </FieldLabel>

      <input
        ref={ref}
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        onFocus={() => setFocused(true)}
        dir={dir}
        placeholder={placeholder}
        className={`block w-full min-w-0 h-10 px-3 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                    transition-all duration-150 outline-none appearance-none
                    ${isDate ? '[color-scheme:light]' : ''}
                    ${
                      error
                        ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200'
                        : focused
                        ? 'border-indigo-400 ring-2 ring-indigo-100'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
        {...props}
      />

      <FieldError msg={error} />
    </div>
  );
});

/* ── PortalSelect ──────────────────────────────────────────── */
function PortalSelect({
  dir = 'rtl',
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled,
  loading,
  errorText,
  id,
}) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const measure = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({
      top: r.bottom + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    measure();

    let ticking = false;
    const onWin = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          measure();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);

    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const outside = (e) => {
      if (
        !btnRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);

    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
    };
  }, [open]);

  return (
    <div dir={dir} className="min-w-0">
      {label && (
        <FieldLabel htmlFor={id} required>
          {label}
        </FieldLabel>
      )}

      <button
        type="button"
        id={id}
        ref={btnRef}
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setOpen((s) => !s)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`block w-full min-w-0 h-10 px-3 text-base sm:text-sm rounded-xl border bg-white flex items-center justify-between
                    transition-all duration-150 outline-none
                    ${
                      errorText
                        ? 'border-red-300 bg-red-50'
                        : focused || open
                        ? 'border-indigo-400 ring-2 ring-indigo-100'
                        : 'border-gray-200 hover:border-indigo-300'
                    }
                    ${
                      disabled || loading
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
      >
        <span className={`truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
          {loading ? 'טוען…' : selected ? selected.label : placeholder || 'בחר…'}
        </span>

        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <FieldError msg={errorText} />

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: coords.top + 4,
              left: coords.left,
              width: coords.width,
              zIndex: 1000000,
            }}
            dir={dir}
          >
            <div className="rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden">
              <ul role="listbox" className="max-h-[50vh] overflow-auto py-1">
                {loading ? (
                  <li className="px-4 py-2.5 text-sm text-gray-400">
                    טוען אפשרויות...
                  </li>
                ) : options.length === 0 ? (
                  <li className="px-4 py-2.5 text-sm text-gray-400">
                    אין אפשרויות זמינות
                  </li>
                ) : (
                  options.map((opt) => {
                    const active = opt.value === value;
                    return (
                      <li
                        key={opt.value}
                        role="option"
                        aria-selected={active}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onChange?.(opt.value);
                          setOpen(false);
                        }}
                        className={`px-4 py-2.5 cursor-pointer text-right text-sm select-none transition-colors
                          ${
                            active
                              ? 'bg-indigo-50 text-indigo-700 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {opt.label}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ── Validation ────────────────────────────────────────────── */
const AGE_GROUPS = [
  { value: 'kids-6-9', label: 'ילדים (6–9)' },
  { value: 'kids-10-12', label: 'ילדים (10–12)' },
  { value: 'middle-school', label: 'חטיבת ביניים (ז–ט)' },
  { value: 'high-school', label: 'תיכון (י–יב)' },
  { value: 'college', label: 'סטודנטים' },
  { value: 'adults', label: 'מבוגרים' },
  { value: 'all', label: 'כל הגילאים' },
];

const schema = Yup.object({
  title: Yup.string()
    .trim()
    .min(3, 'שם קצר מדי')
    .max(80, 'שם ארוך מדי')
    .required('שם קורס חובה'),
  subject: Yup.string().required('נושא חובה'),
  type: Yup.string().oneOf(['group', 'private']).required('סוג הקורס חובה'),
  ageGroup: Yup.string().required('קהל יעד חובה'),
  price: Yup.mixed()
    .test('is-number', 'יש להזין מספר', (v) => v === '' || !isNaN(Number(v)))
    .test(
      'non-negative',
      'מחיר לא יכול להיות שלילי',
      (v) => v === '' || Number(v) >= 0
    )
    .required('מחיר חובה'),
  startDate: Yup.string().required('תאריך התחלה חובה'),
  duration: Yup.string().required('משך הקורס חובה'),
  description: Yup.string().max(600, 'עד 600 תווים'),
});

/* ── Main component ────────────────────────────────────────── */
export default function CreateCourseModal({
  isOpen,
  onClose,
  mode = 'create',
  course = null,
  onSubmit,
}) {
  const {
    addCourse,
    subjects,
    subjectsLoading,
    subjectsError,
    refreshSubjects,
  } = useSearch();
  const { user, role, showAlert } = useUser();

  const isTeacher =
    role === 'teacher' || user?.role === 'teacher' || user?.type === 'teacher';

  const initialValues = useMemo(
    () => ({
      title: course?.title || '',
      subject: course?.subject || '',
      type: (course?.type || 'group').toLowerCase(),
      ageGroup: course?.audience || '',
      price: typeof course?.price === 'number' ? String(course.price) : '',
      startDate: course?.startDate
        ? String(course.startDate).slice(0, 10)
        : '',
      duration: course?.duration || '',
      description: course?.description || '',
    }),
    [course]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Only a real logged-in teacher gets attached as the course's
        // teacher here. Any other creator (e.g. a supplier/learning
        // center) leaves this null rather than using a fake sentinel
        // id — courses.teacher_id is nullable precisely for this case,
        // to be assigned later from the supplier's team roster.
        const teacherInfo =
          isTeacher && user
            ? {
                id: user.id,
                name:
                  [user.firstName, user.lastName]
                    .filter(Boolean)
                    .join(' ') || 'מורה',
                avatar: user.picture || '',
                rating: user.rating || 0,
              }
            : { id: null, name: 'טרם שויך מורה', avatar: '', rating: 0 };

        const payload = {
          title: values.title.trim(),
          description: (values.description || '').trim(),
          image: course?.image || '',
          subject: values.subject,
          price: Number(values.price || 0),
          duration: values.duration,
          startDate: values.startDate,
          studentCount: course?.studentCount ?? 0,
          rating: course?.rating ?? 0,
          type: values.type,
          audience: values.ageGroup,
          status: course?.status || (mode === 'create' ? 'Opened' : undefined),
          teacher: course?.teacher || teacherInfo,
          schedule: course?.schedule || [],
        };

        if (onSubmit) {
          await onSubmit({ payload, mode, id: course?.id });
        } else if (mode === 'create') {
          await addCourse(payload);
        } else {
          console.warn(
            'CreateCourseModal: edit mode without onSubmit handler.'
          );
        }

        showAlert?.(
          mode === 'create' ? 'הקורס נוצר בהצלחה!' : 'השינויים נשמרו!',
          'success'
        );
        resetForm();
        onClose?.();
      } catch (err) {
        console.error('Course submit error:', err);
        const msg =
          err?.message ||
          (mode === 'create'
            ? 'יצירת קורס נכשלה.'
            : 'שמירת שינויים נכשלה.');
        showAlert?.(msg, 'error') || alert(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const firstInputRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('ccf-modal-open');
    return () => document.body.classList.remove('ccf-modal-open');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) refreshSubjects();
  }, [isOpen, refreshSubjects]);

  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let t;
    const isSmallScreen = window.innerWidth < 640;

    if (!hasAutoFocusedRef.current && !isSmallScreen) {
      t = setTimeout(() => {
        firstInputRef.current?.focus();
        hasAutoFocusedRef.current = true;
      }, 100);
    }

    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      if (t) clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showErrors = formik.submitCount > 0;
  const fe = (k) => (showErrors ? formik.errors[k] : '');
  const descCount = (formik.values.description || '').length;
  const subjectOptions = (subjects || []).map((s) => ({
    value: s.name,
    label: s.name,
  }));
  const titleText = mode === 'edit' ? 'עריכת קורס' : 'יצירת קורס חדש';
  const subtitleText =
    mode === 'edit' ? 'עדכון פרטי הקורס' : 'הגדרת פרטי הקורס והתוכן';

  const submitLabel = formik.isSubmitting
    ? mode === 'edit'
      ? 'שומר…'
      : 'יוצר...'
    : mode === 'edit'
    ? 'שמור שינויים'
    : 'יצירת קורס';

  const typeBtnCls = (t) =>
    `flex-1 h-9 text-sm font-semibold transition-all duration-150 rounded-lg
     ${
       formik.values.type === t
         ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-sm'
         : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
     }`;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]"
        onClick={onClose}
      />

      {formik.isSubmitting && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div
        className="relative z-[1] w-full sm:max-w-2xl lg:max-w-3xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2zM8 7h8M8 11h6" />
              </svg>
            </div>

            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                {titleText}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">{subtitleText}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form
          id="create-course-form"
          onSubmit={formik.handleSubmit}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          <div className="px-5 sm:px-7 py-5 space-y-5">
            {/* Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                ref={firstInputRef}
                label="שם הקורס"
                name="title"
                placeholder="לדוגמה: מתמטיקה מתקדמת לתיכון"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fe('title')}
                required
              />

              <PortalSelect
                id="subject"
                label="נושא"
                placeholder={subjectsLoading ? 'טוען מקצועות…' : 'בחר נושא…'}
                value={formik.values.subject}
                onChange={(v) => formik.setFieldValue('subject', v)}
                options={subjectOptions}
                disabled={(subjects || []).length === 0 && !subjectsLoading}
                loading={subjectsLoading}
                errorText={fe('subject') || subjectsError}
              />
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <FieldLabel required>סוג הקורס</FieldLabel>
                <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() => formik.setFieldValue('type', 'group')}
                    className={typeBtnCls('group')}
                  >
                    קבוצתי
                  </button>
                  <button
                    type="button"
                    onClick={() => formik.setFieldValue('type', 'private')}
                    className={typeBtnCls('private')}
                  >
                    פרטי
                  </button>
                </div>
                <FieldError msg={fe('type')} />
              </div>

              <PortalSelect
                id="ageGroup"
                label="קהל יעד"
                placeholder="בחר גיל יעד…"
                value={formik.values.ageGroup}
                onChange={(v) => formik.setFieldValue('ageGroup', v)}
                options={AGE_GROUPS}
                errorText={fe('ageGroup')}
              />
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="min-w-0">
                <InputField
                  label="מחיר לשיעור (₪)"
                  name="price"
                  type="text"
                  inputMode="decimal"
                  placeholder="120"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={fe('price')}
                  required
                />
              </div>

              <div className="min-w-0">
                <InputField
                  label="תאריך התחלה"
                  name="startDate"
                  type="date"
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={fe('startDate')}
                  required
                  dir="ltr"
                />
              </div>

              <div className="min-w-0">
                <InputField
                  label="משך הקורס"
                  name="duration"
                  placeholder="לדוגמה: 10 שבועות"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={fe('duration')}
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div className="min-w-0">
              <FieldLabel htmlFor="description">תיאור הקורס (אופציונלי)</FieldLabel>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="ספרו על הקורס: מטרותיו, התוכן שיילמד, הכישורים שהתלמידים ירכשו..."
                  className={`w-full min-w-0 px-3 py-2.5 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                              resize-none outline-none transition-all duration-150
                              ${
                                showErrors && formik.errors.description
                                  ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200'
                                  : 'border-gray-200 hover:border-indigo-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100'
                              }`}
                />
                <span
                  className={`absolute bottom-2.5 left-3 text-[11px] font-medium ${
                    descCount > 550
                      ? 'text-orange-400'
                      : descCount > 500
                      ? 'text-yellow-400'
                      : 'text-gray-300'
                  }`}
                >
                  {descCount}/600
                </span>
              </div>
              <FieldError msg={showErrors ? formik.errors.description : ''} />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={formik.isSubmitting}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ביטול
          </button>

          <button
            type="submit"
            form="create-course-form"
            disabled={formik.isSubmitting}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                       transition-all duration-150 flex items-center gap-2"
          >
            {formik.isSubmitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}

            {!formik.isSubmitting && (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}

            {submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}